import { pacificToday } from '../../../shared/dates';
import type { IsoDate, Signal, Visit } from '../../../shared/types';
import { STAGE_LABELS } from '../ui/labels';
import { addDays, formatTime, parseIsoDate, toIsoDate } from './dates';
import { periodStart, visitDay } from './home';
import type { Delta, Period } from './home';
import type { FollowUp, TodayModel } from './today';

/** Desktop Overview math (D65) — pure, over the mirror and the stored new-business list. */

interface Range {
  start: IsoDate;
  end: IsoDate;
}

/** The last n periods, oldest first; the last one is the current period, up to today. */
const periodRanges = (today: IsoDate, period: Period, n: number): Range[] => {
  const current = periodStart(today, period);
  const starts = Array.from({ length: n }, (_, i) => {
    const back = n - 1 - i;
    if (period === 'week') return addDays(current, -7 * back);
    const d = parseIsoDate(current);
    return toIsoDate(new Date(d.getFullYear(), d.getMonth() - back, 1));
  });
  return starts.map((start, i) => {
    const next = starts[i + 1];
    return { start, end: next ? addDays(next, -1) : today };
  });
};

const within = (day: IsoDate, r: Range): boolean => day >= r.start && day <= r.end;

/** How many dates fall in each of the last n periods — the sparkline under a number. */
export const periodSeries = (dates: readonly IsoDate[], today: IsoDate, period: Period, n = 5): number[] =>
  periodRanges(today, period, n).map((r) => dates.filter((d) => within(d, r)).length);

/** % of each period's visits where the rep talked to the owner; null for a period without visits. */
export const ownerTalkSeries = (
  visits: readonly Visit[],
  today: IsoDate,
  period: Period,
  n = 5,
): (number | null)[] =>
  periodRanges(today, period, n).map((r) => {
    const inPeriod = visits.filter((v) => within(visitDay(v), r));
    if (inPeriod.length === 0) return null;
    return Math.round((100 * inPeriod.filter((v) => v.spoke_with === 'owner').length) / inPeriod.length);
  });

/** A rate's change in points, worded like `deltaOf`; flat when either side is unknown. */
export const rateDelta = (current: number | null, previous: number | null, vs: string): Delta => {
  if (current === null) return { text: 'No visits yet', short: '—', tone: 'flat' };
  if (previous === null) return { text: `No visits ${vs}`, short: 'New', tone: 'flat' };
  const diff = current - previous;
  if (diff === 0) return { text: `Same as ${vs}`, short: 'Same', tone: 'flat' };
  const short = `${diff > 0 ? '▲' : '▼'} ${Math.abs(diff)} pts`;
  return { text: `${short} vs ${vs}`, short, tone: diff > 0 ? 'up' : 'down' };
};

export interface NewBusinessSummary {
  /** Arrived in the inbox this period and still to review. */
  count: number;
  /** The businesses most worth a visit, as the server sorts them (D72). */
  top: Signal[];
}

const arrivedOn = (s: Signal): IsoDate => {
  const d = new Date(s.created_at);
  return Number.isNaN(d.getTime()) ? s.filed_on : pacificToday(d);
};

/** How many new businesses arrived this period, and the first of the queue as the server sorts it. */
export const newBusinessSummary = (
  signals: readonly Signal[],
  today: IsoDate,
  period: Period,
  limit = 5,
): NewBusinessSummary => {
  const start = periodStart(today, period);
  return {
    count: signals.filter((s) => {
      const day = arrivedOn(s);
      return day >= start && day <= today;
    }).length,
    top: signals.slice(0, limit),
  };
};

export interface AgendaRow {
  follow: FollowUp;
  /** "9:30 AM", "Any time", or "2 days late". */
  when: string;
  /** The visit's next action ("Demo KDS"), else the lead's stage. */
  what: string;
  late: boolean;
}

/** Today's stops as the Overview lists them: late ones first, then by time, as on Today (D38). */
export const agendaRows = (model: Pick<TodayModel, 'overdue' | 'dueToday'>): AgendaRow[] =>
  [...model.overdue, ...model.dueToday].map((f) => ({
    follow: f,
    when:
      f.daysOverdue > 0
        ? `${f.daysOverdue} day${f.daysOverdue === 1 ? '' : 's'} late`
        : f.visit.next_action_time
          ? formatTime(f.visit.next_action_time)
          : 'Any time',
    what: f.visit.next_action?.trim() || STAGE_LABELS[f.lead.stage],
    late: f.daysOverdue > 0,
  }));
